import React, { useCallback, useLayoutEffect } from 'react';
import Animated from 'react-native-reanimated';
import { Keyboard, StyleSheet, View } from 'react-native';
import { rem, AppView, SettingsItemTitle, SettingsItemNavigate, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useColors from 'hooks/useColors';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { AccountTypeDisplay, AccountTypeDisplayLong, ProductTransferDisplayMap, transferProductString, } from 'features/invest/constants';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { isWeb } from '../../../constants';
const strings = {
    title: (t, type) => t === 'JISA'
        ? `Does this ${AccountTypeDisplay[t]} include current tax year contributions?`
        : `Does this ${transferProductString[type]} ${t} include current tax year contributions?`,
    subTitle: (t, type) => t === 'JISA'
        ? `We need to know whether this ${AccountTypeDisplayLong[t]} includes money paid in during the current tax year. This is about when the money was originally added, not when you’re making the transfer`
        : `We need to know whether this ${transferProductString[type]} ${t} includes money paid in during the current tax year.`,
    notSureWhichToChoose: 'Not sure which option to choose? ', // space intentional
    learnMore: 'Learn more',
    learnMoreInfo: (t) => [
        `If you’re not sure, check your latest ${t === 'JISA' ? ProductTransferDisplayMap[t] : t} statement or contact your current provider.`,
        `Choose “Yes” if any money in this ${t === 'JISA' ? AccountTypeDisplay[t] : t} was paid in since 6 April this tax year.`,
        `Choose “No” only if all of the money was paid in before then.`,
    ],
};
const ProductTransferTaxYearScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const colors = useColors();
    const { type, provider, balance, accountId, ...identification } = route.params;
    const accountType = accountId ? 'JISA' : 'ISA';
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const handlePress = useCallback((transferTaxYear) => () => {
        Keyboard.dismiss();
        Modal.hide();
        if (accountType === 'JISA'
            ? provider.jisaCashTransferMethod === 'MANUAL' &&
                (provider.jisaSignatureType === 'ELECTRONIC' || provider.jisaSignatureType === 'SCANNED')
            : provider.isaCashTransferMethod === 'MANUAL' &&
                (provider.isaSignatureType === 'ELECTRONIC' || provider.isaSignatureType === 'SCANNED')) {
            navigation.navigate('ProductTransferSignature', {
                type,
                balance,
                provider,
                accountId,
                transferTaxYear,
                ...identification,
            });
            return;
        }
        navigation.navigate('ProductTransferConfirmDetails', {
            type,
            balance,
            provider,
            accountId,
            transferTaxYear,
            ...identification,
        });
    }, [accountType, provider, type, balance, accountId, identification]);
    const showLearnMore = useCallback(() => {
        Keyboard.dismiss();
        Modal.showConfirmation({
            title: 'How to answer this?',
            subtitle: strings.learnMoreInfo(accountType),
            confirmButtonTitle: 'Okay',
        });
    }, [accountType]);
    return (<AppView withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={paddingHorizontalStyle} keyboardShouldPersistTaps="handled" scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title={strings.title(accountType, type)} subTitle={strings.subTitle(accountType, type)}/>

          <TouchableBounceCard onPress={handlePress('CURRENT')} onDark noMarginTop>
            <View pointerEvents="none">
              <SettingsItemNavigate>
                <SettingsItemTitle>Yes, it includes current tax year contributions</SettingsItemTitle>
              </SettingsItemNavigate>
            </View>
          </TouchableBounceCard>

          <TouchableBounceCard onPress={handlePress('PRIOR')} onDark noMarginTop>
            <View pointerEvents="none">
              <SettingsItemNavigate>
                <SettingsItemTitle>No, it only includes previous tax years’ contributions</SettingsItemTitle>
              </SettingsItemNavigate>
            </View>
          </TouchableBounceCard>

          <View style={styles.inputInfo}>
            <Text TextThin-14 Secondary>
              {strings.notSureWhichToChoose}
              <Text Text-14 Primary onPress={showLearnMore} suppressHighlighting>
                {strings.learnMore}
              </Text>
            </Text>
          </View>
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    inputInfo: {
        marginHorizontal: rem(16),
    },
});
export default ProductTransferTaxYearScreen;
